import { Box } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import UploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import { httpClient } from "api/httpClient";
import { DocumentAddButton } from "components/fields/DocumentsField/AddButton";
import { DropPaper } from "components/fields/DocumentsField/DropPaper";
import { FileInfo } from "components/fields/DocumentsField/SimpleFileInfo";
import React, { useState } from "react";
import { Button, useNotify, useTranslate } from "react-admin";
import { Form } from "react-final-form";
import { downloadFile } from "utils/download";
import { isNullUndEmpty, mapWithFilter } from "utils/general";
import { useNotifyError } from "utils/notifiers/useNotifyError";
import { ImportErrorsAlert } from "./ImportErrorsAlert";

export const ImportUploadDialog = ({
	title,
	uploadEndpoint,
	successMessage,
	onDialogClose,
	onUploadSuccess,
	initialValues = {},
	acceptTypes = ".xls,.xlsx",
	renderCustomForm = null,
	// Параметри для режиму експорту
	exportMode = false,
	exportButtonLabel = "osfc.buttons.upload",
	exportFileName = "export.xlsx",
}) => {
	const notifyError = useNotifyError();
	const notify = useNotify();
	const t = useTranslate();

	const [selectedFile, setSelectedFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [importErrors, setImportErrors] = useState(null);

	const uploadData = React.useCallback(
		(values) => {
			const data = new FormData();
			data.append("file", values.file);

			mapWithFilter(Object.entries(initialValues), ([key, value]) => {
				if (!isNullUndEmpty(value)) {
					data.append(key, value);
				}
				return null;
			});

			setUploading(true);
			setImportErrors(null);

			httpClient(uploadEndpoint, { method: "POST", body: data })
				.then(() => {
					notify(successMessage, "info");
					onUploadSuccess();
				})
				.catch((error) => {
					const errorMessage = typeof error === 'string' ? error : error.message;
					if (errorMessage) {
						const errors = errorMessage.split('\n').filter(Boolean);
						setImportErrors(errors);
					} else {
						notifyError(error);
					}
				})
				.finally(() => {
					setUploading(false);
				});
		},
		[initialValues, uploadEndpoint, successMessage, onUploadSuccess, notify, notifyError]
	);

	const uploadAndExportData = React.useCallback(
		async (values) => {
			const data = new FormData();
			data.append("file", values.file);

			mapWithFilter(Object.entries(initialValues), ([key, value]) => {
				if (!isNullUndEmpty(value)) {
					data.append(key, value);
				}
				return null;
			});

			setUploading(true);

			try {
				await downloadFile(uploadEndpoint, exportFileName, {
					method: "POST",
					body: data,
				});

				notify(successMessage, "info");
				onUploadSuccess();
			} catch (err) {
				notifyError(err);
			} finally {
				setUploading(false);
			}
		},
		[
			initialValues,
			uploadEndpoint,
			successMessage,
			onUploadSuccess,
			notify,
			notifyError,
			exportFileName,
		]
	);

	const handleOnSubmit = React.useCallback(
		(values) => {
			if (selectedFile) {
				if (exportMode) {
					uploadAndExportData({ ...values, file: selectedFile });
				} else {
					uploadData({ ...values, file: selectedFile });
				}
			} else {
				notify("File is required", "warning");
			}
		},
		[selectedFile, exportMode, uploadData, uploadAndExportData, notify]
	);

	return (
		<Form onSubmit={handleOnSubmit} initialValues={initialValues}>
			{(formProps) => {
				if (renderCustomForm) {
					return renderCustomForm({
						formProps,
						onDialogClose,
						selectedFile,
						setSelectedFile,
						uploading,
						t,
						title,
						acceptTypes,
						exportMode,
						exportButtonLabel,
					});
				}

				return (
					<DefaultUploadDialogForm
						formProps={formProps}
						onDialogClose={onDialogClose}
						selectedFile={selectedFile}
						setSelectedFile={setSelectedFile}
						uploading={uploading}
						t={t}
						title={title}
						acceptTypes={acceptTypes}
						exportMode={exportMode}
						exportButtonLabel={exportButtonLabel}
						importErrors={importErrors}
						setImportErrors={setImportErrors}
					/>
				);
			}}
		</Form>
	);
};

const DefaultUploadDialogForm = ({
	formProps,
	onDialogClose,
	selectedFile,
	setSelectedFile,
	uploading,
	t,
	title,
	acceptTypes,
	exportMode = false,
	exportButtonLabel = "osfc.buttons.upload",
	importErrors = null,
	setImportErrors = null,
}) => {
	return (
		<Dialog open maxWidth="md" onClose={onDialogClose} fullWidth>
			<DialogTitle>{t(title)}</DialogTitle>
			<DialogContent>
				<ImportErrorsAlert errors={importErrors} onClose={setImportErrors} />
				<DropPaper processDocument={setSelectedFile} elevation={0} variant="outlined">
					<Box
						p={2}
						pb={selectedFile ? 0 : 5}
						display="flex"
						justifyContent="space-between"
						alignItems="center"
					>
						<DocumentAddButton
							uploading={false}
							uploadFile={setSelectedFile}
							acceptTypes={acceptTypes}
						/>
					</Box>
					{selectedFile && (
						<Box p={2}>
							<FileInfo file={selectedFile} setFile={setSelectedFile} />
						</Box>
					)}
				</DropPaper>
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					disabled={uploading}
					label={t("ra.action.cancel")}
					onClick={onDialogClose}
					color="secondary"
				/>
				<Button
					variant="contained"
					label={t(exportMode ? exportButtonLabel : "osfc.buttons.upload")}
					onClick={formProps.handleSubmit}
					color="primary"
					disabled={uploading || !selectedFile}
					startIcon={
						uploading ? (
							<CircularProgress size={16} thickness={5} color="inherit" />
						) : exportMode ? (
							<GetAppIcon />
						) : (
							<UploadIcon />
						)
					}
				/>
			</DialogActions>
		</Dialog>
	);
};
